import { SlateElementType } from '@vedalib/rich-text'
import React, { useCallback, useState } from 'react'

import {
  getSelectedFormat,
  getSetToolbarForm,
  getUpdateFormat,
  useRichTextContext,
} from 'components/form/RichText/useRichTextContext'
import { IconButton } from 'components/uiKit/Button'
import { KitSize } from 'components/uiKit/KitTypes'
import Select from 'components/uiKit/Select/Select'
import { stopPropagation } from 'constants/commonConstans'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlock, getSections } from 'services/Store/Project/selectors'
import type { Section } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import * as s from './CrossLinkForm.module.scss'

interface IValue {
  section?: string
  block?: string
}

const sectionsToOptions = (sections: Section[]) => {
  return sections
    .filter((section: Section) => section.type !== SectionTypeEnum.chapter)
    .map((section: Section) => ({
      label: section.name,
      value: section.id,
    }))
}

const CrossLinkForm: React.FC = () => {
  const selectedFormat = useRichTextContext(getSelectedFormat)
  const [value, setValue] = useState<IValue | undefined>(selectedFormat?.inline?.value)
  const onUpdateFormat = useRichTextContext(getUpdateFormat)
  const setToolbarForm = useRichTextContext(getSetToolbarForm)

  const sections = useProjectContext(getSections)
  const currentBlock = useProjectContext(getBlock)
  const section = sections.find((section) => section.id === value?.section)
  const isLanding = section?.type === SectionTypeEnum.landing

  const isEditing = Boolean(selectedFormat?.inline?.value)

  const blockOptions = section?.blocksOrder.map((value, i) => ({
    value,
    label: `${i + 1} ${t('elements.button.form.blockCount')}`,
    disabled: value === currentBlock?.uuid,
  }))

  const handleClose = useCallback(() => setToolbarForm('default'), [setToolbarForm])

  const onSave = () => {
    onUpdateFormat(SlateElementType.crossLink, value)
    handleClose()
  }

  const onDelete = () => {
    onUpdateFormat(SlateElementType.crossLink, null)
    handleClose()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      handleClose()
    }
  }

  const onSectionChange = (section: string) => {
    setValue({ section })
  }

  const onBlockChange = (block: string) => {
    setValue((prev) => ({ ...prev, block }))
  }

  // stopPropagation - HOTFIX for https://www.notion.so/2413-1885457f94c5812498f2e5ff0fc8f373
  return (
    <div className={s.root} onClick={stopPropagation} onKeyDown={onKeyDown}>
      <IconButton
        icon='builderInputBack'
        name='backButton'
        onClick={handleClose}
        size={KitSize.S}
        styleType='secondary'
      />
      <Select
        minWidth={360}
        name='rtCrossLinkSection'
        onChange={onSectionChange}
        options={sectionsToOptions(sections)}
        placeholder={t('uiKit.tooltip.sectionName')}
        size={KitSize.S}
        styleType='ghost'
        value={value?.section}
        isSearch
        resettable
      />
      <Select
        disabled={!!(value?.section && !isLanding)}
        minWidth={88}
        name='rtCrossLinkBlock'
        onChange={onBlockChange}
        options={isLanding ? blockOptions : undefined}
        placeholder={t('uiKit.tooltip.block')}
        size={KitSize.S}
        styleType='ghost'
        value={value?.block}
        isSearch
        resettable
      />
      {isEditing && (
        <IconButton
          icon='otherBrokenLink'
          name='delete'
          onClick={onDelete}
          size={KitSize.S}
          styleType='ghost'
        />
      )}
      <IconButton
        disabled={!value?.section}
        icon={!value?.section ? 'iconSendDisabled' : 'iconSend'}
        name='save'
        onClick={onSave}
        size={KitSize.S}
        styleType='ghost'
      />
    </div>
  )
}

export default CrossLinkForm
